import $ from 'jquery';
import _ from 'underscore';
import { a11yPlugin } from '@bingads-webui/projection-grid-a11y';
import { aggregatedRowsPluginBuilder } from '@bingads-webui-campaign/pgrid-aggregated-rows';
import { jsdataRawDataPlugin } from '@bingads-webui-campaign/pgrid-js-data-raw-data';
import { assistedSelectionPlugin } from '@bingads-webui/projection-grid-assisted-selection';
import { dateFilterPlugin } from '@bingads-webui-campaign/projection-grid-date-filter';
import { editablePlugin } from '@bingads-webui-campaign/projection-grid-editable';
import { paginationPlugin } from '@bingads-webui/projection-grid-pagination';
import { segmentPlugin } from '@bingads-webui-campaign/projection-grid-segment';
import { segmentationType, campaignSegmentationTypeEnum } from '@bingads-webui-campaign/segmentation-utilities';
import { uiBlockerPlugin } from '@bingads-webui/projection-grid-uiblocker';
import { perfMarkerPlugin } from '@bingads-webui-campaign/pgrid-performance-log';
import { inlineDownloadWithoutFilterstatePlugin } from '@bingads-webui-campaign/projection-grid-inline-download';
import { columnChooserPlugin } from '@bingads-webui-campaign/projection-grid-column-chooser';
import { periodOverPeriodPlugin } from '@bingads-webui-campaign/projection-grid-period-over-period';
import {
  TargetingSettingsContext,
  getReportEmailRule,
  segmentValueCellTmpl,
  TargetingScope,
  TargetingLevel,
  TargetingType,
  getCriterionType,
  getLocationDisplayName,
} from '@bingads-webui-campaign/targeting-settings-scaffolds';

export function getPlugins({
  view,
  columnConfig,
  scope,
  level,
  perfMarker,
  filterService,
  p$daterange, // add this will disable using the state above in the plugin
  addDateChangeListener,
}) {
  const columnChooserConfig = columnConfig.getColumnChooserConfig(/* isForVNext */);
  const { i18n } = TargetingSettingsContext;
  const aggregatedRowsPlugin = aggregatedRowsPluginBuilder({
    i18n,
    keys: {
      overallI18nKey: 'TargetingSettings_Grid_Overall',
      filteredI18nKey: 'TargetingSettings_Grid_Filtered',
      deletedI18nKey: 'TargetingSettings_Grid_OtherLocations',
    },
    helpIds: {
      deletedHelpId: 'pop_BA_CampaignsGrid_TotalOtherLocations',
    },
    permissions: TargetingSettingsContext.pageContext.Permissions,
    filterService,
    customConfig: {
      // #SEARCHAUDIENCEROWBUG
      // Booleans default to true, but developers omitted the string from keys.txt
      // Making the old (accidental?) behavior explicit until we want to confirm to change it.
      showSearchRow: false,
      showAudienceRow: false,
    },
  });
  columnChooserConfig.customToolbarIcon = 'ba-TripleColumnSolid';
  const entity = (scope === TargetingScope.AdGroup || level === TargetingLevel.AdGroup)
    ? 'AdGroupCriterion'
    : 'CampaignCriterion';
  return _.compact([{
    // For accurate grid init latency, PerfMarkerPlugin should be the first plugin to load.
    plugin: perfMarkerPlugin,
    config: {
      appConfig: TargetingSettingsContext.config,
      marker: perfMarker,
    },
  }, {
    plugin: paginationPlugin,
    config: {
      availablePageSizes: [20, 50, 100, 200],
      enableRandomPage: false,
      pageSizeText: i18n.getString('Grid_ShowRows'),
      totalPageCountText: i18n.getString('Grid_PageOf'),
      ...({
        pageSize: 20,
        pageNumberLabelText: i18n.getString('Grid_Page_Colon'),
      }),
    },
  }, ...[
    {
      plugin: inlineDownloadWithoutFilterstatePlugin,
      config: {
        isForVNext: true,
        p$daterange,
        DownloadView: TargetingSettingsContext.DownloadView,
        i18n: TargetingSettingsContext.i18n,
        s$i18n: TargetingSettingsContext.s$i18n,
        isAvailable: true,
        schema: TargetingSettingsContext[`s$${entity}`],
        gridId: 'LocationsGrid',
        ReportEmailRule: getReportEmailRule(entity),
      },
    }], {
    plugin: TargetingSettingsContext.bidAdjustmentBulkEditPlugin,
    ...({
      config: {
        gridType: _.invert(TargetingType)[TargetingType.Locations],
        entity,
        formatEntityString: Criterion => getLocationDisplayName({ Criterion }),
        isForVNext: true,
      },
    }),
  }, {
    plugin: segmentPlugin,
    config: {
      cellTemplates: {
        Location: segmentValueCellTmpl,
      },
      isOdataActionSource: true,
      segmentationTypes: _.difference(campaignSegmentationTypeEnum, [
        segmentationType.GoalName,
        segmentationType.GoalType,
        segmentationType.AudienceName,
        segmentationType.AudienceType,
      ]),
      permissions: TargetingSettingsContext.pageContext.Permissions,
      edm: TargetingSettingsContext.edmCampaign,
      i18n: TargetingSettingsContext.i18n,
      state: TargetingSettingsContext.state,
      setSegType: TargetingSettingsContext.segmentPreference.setSegType,
      getSegType: TargetingSettingsContext.segmentPreference.getSegType,
    },
  }, !TargetingSettingsContext.config.get('CurrentAccountInActive') &&
  !TargetingSettingsContext.pageContext.Permissions.IsReadOnlyUser && {
    plugin: editablePlugin,
    config: { i18n },
  }, {
    plugin: uiBlockerPlugin,
    config: { el: $('.locations-page') },
  }, {
    plugin: periodOverPeriodPlugin,
    config: { i18n },
  }, {
    plugin: columnChooserPlugin,
    config: columnChooserConfig,
  }, {
    plugin: TargetingSettingsContext.removeBulkEditPlugin,
    ...({
      config: {
        isForVNext: true,
        type: 'button',
        id: 'bulk-edit-remove',
        gridType: _.invert(TargetingType)[TargetingType.Locations],
        entity: getCriterionType(view),
        view,
        disabled: TargetingSettingsContext.pageContext.Permissions.IsReadOnlyUser
                  || TargetingSettingsContext.config.get('CurrentAccountInActive'),
      },
    }),
  }, {
    plugin: dateFilterPlugin,
    config: {
      isOdataActionSource: true,
      comparisonColumns: [
        'PeriodPerformanceMetrics',
        'PerformanceMetricsChange',
        'PerformanceMetricsChangePercentage',
      ],
      edm: TargetingSettingsContext.edmCampaign,
      state: TargetingSettingsContext.state,
      addDateChangeListener: addDateChangeListener || ((handler) => {
        if (window.App && window.App.views && window.App.views.Summary) {
          // TODO refactor and remove global dependency
          window.App.views.Summary.bindDateFilterChangeHandler(handler);
        }
      }),
      // add this will disable using the state above in the plugin
      p$daterange,
    },
  }, {
    plugin: assistedSelectionPlugin,
  }, {
    plugin: a11yPlugin,
    config: { i18n },
  }, {
    plugin: aggregatedRowsPlugin,
  }, {
    plugin: jsdataRawDataPlugin,
  }]);
}
